import { createFileRoute } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import { ImageCompany } from '@/routes/_app/-components/assets/image-company'
import { ImageApplicant } from '@/routes/_app/-components/assets/image-applicant'
import { Frame } from '@/components/ui/frame'
import { HomeLink } from '@/routes/_app/-components/shared/home-link'
import { ArrowRightIcon } from 'lucide-react'
import * as v from 'valibot'
import { RegisterChoicePending } from './-components/shared/flow-pending'

export const Route = createFileRoute('/_app/register/')({
  validateSearch: v.object({ redirect: v.optional(v.string()) }),
  component: RouteComponent,
  pendingComponent: RegisterChoicePending,
})

function RouteComponent() {
  const { t } = useLingui()
  // Carry the post-auth destination (e.g. the profile a visitor wanted to compare against) into the
  // chosen registration branch so it survives the whole onboarding.
  const { redirect } = Route.useSearch()

  return (
    <div className="mx-auto flex max-w-[480px] flex-col items-center gap-8 p-6">
      <HomeLink />
      <div className="text-center">
        <h1 className="text-2xl font-semibold">{t`Create your account`}</h1>
        <p className="text-muted-foreground mt-1">{t`Choose your option to register with D'Anchiano`}</p>
      </div>

      <div className="flex w-full flex-col gap-4 sm:flex-row">
        <Link to="/register/company" search={{ redirect }} className="group flex-1">
          <Frame className="h-full transition-shadow hover:shadow-md">
            <div className="flex flex-col items-center gap-4 px-4 py-2 text-center">
              <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full">
                <ImageCompany className="size-7" fill="rgb(var(--color-primary))" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">{t`Companies`}</h2>
                <p className="text-muted-foreground mt-1 text-sm">{t`Evaluate and manage talent for your organization`}</p>
              </div>
              <span className="text-primary inline-flex items-center gap-1 text-sm font-medium">
                {t`Get started`}
                <ArrowRightIcon className="size-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </div>
          </Frame>
        </Link>

        <Link to="/register/applicant" search={{ redirect }} className="group flex-1">
          <Frame className="h-full transition-shadow hover:shadow-md">
            <div className="flex flex-col items-center gap-4 px-4 py-2 text-center">
              <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full">
                <ImageApplicant className="size-7" fill="rgb(var(--color-primary))" width={28} height={28} />
              </div>
              <div>
                <h2 className="text-lg font-semibold">{t`Professionals`}</h2>
                <p className="text-muted-foreground mt-1 text-sm">{t`Discover your psychographic profile and your market fit`}</p>
              </div>
              <span className="text-primary inline-flex items-center gap-1 text-sm font-medium">
                {t`Get started`}
                <ArrowRightIcon className="size-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </div>
          </Frame>
        </Link>
      </div>

      <p className="text-muted-foreground text-sm">
        {t`Already have an account?`}{' '}
        <Link to="/login" search={{ redirect }} className="text-primary font-medium hover:underline">
          {t`Login`}
        </Link>
      </p>
    </div>
  )
}
